/**
 * 가격 체계 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/layout/AdminLayout';
import DataTable, { Column } from '../../../components/common/DataTable';
import Pagination from '../../../components/common/Pagination';
import FilterBar, { FilterField } from '../../../components/common/FilterBar';
import { productService } from '../../../services/productService';
import { ProductPrice, ProductPriceFilter, PriceOwnerType, PriceCategory, PriceType } from '../../../types/product';

const PricesList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<ProductPriceFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['prices', filter],
    queryFn: () => productService.getPrices(filter),
  });

  const getOwnerTypeLabel = (type: PriceOwnerType) => {
    const typeMap: Record<PriceOwnerType, string> = {
      hall: '관',
      package: '패키지',
      agent: '에이전트',
      partner: '제휴업체',
    };
    return typeMap[type] || type;
  };

  const getCategoryLabel = (category: PriceCategory) => {
    const categoryMap: Record<PriceCategory, string> = {
      base: '기본',
      group: '단체',
      promotion: '프로모션',
    };
    return categoryMap[category] || category;
  };

  const getPriceTypeLabel = (type: PriceType) => {
    const typeMap: Record<PriceType, string> = {
      adult: '성인',
      youth: '청소년',
      disabled: '장애인',
      infant: '유아',
    };
    return typeMap[type] || type;
  };

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  const columns: Column<ProductPrice>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'owner_type',
      label: '소유자 유형',
      render: (price) => (
        <span style={{ color: '#ccc' }}>{getOwnerTypeLabel(price.owner_type)}</span>
      ),
    },
    {
      key: 'owner_id',
      label: '소유자 ID',
      render: (price) => (
        <span style={{ color: '#ccc' }}>{price.owner_id}</span>
      ),
    },
    {
      key: 'price_category',
      label: '가격 카테고리',
      render: (price) => (
        <Badge bg="info">{getCategoryLabel(price.price_category)}</Badge>
      ),
    },
    {
      key: 'price_type',
      label: '인원 유형',
      render: (price) => (
        <span style={{ color: '#ccc' }}>{getPriceTypeLabel(price.price_type)}</span>
      ),
    },
    {
      key: 'price_amount',
      label: '가격',
      render: (price) => (
        <span style={{ color: '#00BCD4', fontWeight: 'bold' }}>
          {price.price_amount.toLocaleString()}원
        </span>
      ),
    },
    {
      key: 'min_quantity',
      label: '최소 수량',
      render: (price) => (
        <span style={{ color: '#ccc' }}>
          {price.min_quantity ? `${price.min_quantity}명 이상` : '-'}
        </span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (price) => getStatusBadge(price.status),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'owner_type',
      label: '소유자 유형',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'hall', label: '관' },
        { value: 'package', label: '패키지' },
        { value: 'agent', label: '에이전트' },
        { value: 'partner', label: '제휴업체' },
      ],
    },
    {
      name: 'price_category',
      label: '가격 카테고리',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'base', label: '기본' },
        { value: 'group', label: '단체' },
        { value: 'promotion', label: '프로모션' },
      ],
    },
    {
      name: 'price_type',
      label: '인원 유형',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'adult', label: '성인' },
        { value: 'youth', label: '청소년' },
        { value: 'disabled', label: '장애인' },
        { value: 'infant', label: '유아' },
      ],
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'active', label: '활성' },
        { value: 'inactive', label: '비활성' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (price: ProductPrice) => {
    navigate(`/admin/products/prices/${price.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>가격 체계 관리</h1>
          <Button variant="primary" onClick={() => navigate('/admin/products/prices/new')}>
            가격 등록
          </Button>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.prices || []}
              loading={isLoading}
              emptyMessage="가격 정보가 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PricesList;

